import React, { useEffect, useRef, useState } from "react";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Modal from "@/components/Modal";
import ImageInput from "@/components/ImageInput";
import PlayerPhoto from "@/components/PlayerPhoto";
import { Plus, MagnifyingGlass, UploadSimple, DownloadSimple, Trash, EyeSlash, ArrowClockwise, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const ROLES = ["Batsman", "Bowler", "All-Rounder", "Wicket-Keeper"];
const STATUSES = ["all", "available", "sold", "unsold", "hidden"];
const CATEGORIES = ["A", "B", "C", "D", "Icon", "Guest"];

function StatusBadge({ status }) {
  const map = {
    available: "bg-yellow-400 text-black border-yellow-500",
    sold: "bg-emerald-500 text-white border-emerald-600",
    unsold: "bg-red-600 text-white border-red-700",
    hidden: "bg-gray-200 text-gray-700 border-gray-300",
  };
  return <span className={`text-[10px] font-bold tracking-widest uppercase px-2 py-1 rounded border ${map[status] || map.available}`}>{status}</span>;
}

function PlayerForm({ auction, initial, onSave, formId, isEdit, teams = [] }) {
  // Dark premium popup styling (organiser-provided reference design).
  const fieldCls = "w-full bg-white/5 border border-white/15 rounded-md px-3.5 min-h-[46px] text-slate-100 placeholder:text-slate-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition";
  const selectCls = `${fieldCls} [color-scheme:dark]`;
  const labelCls = "block mb-2 text-slate-400 text-[11px] tracking-[0.22em] font-black uppercase";
  const Req = <span className="text-blue-400">*</span>;
  // Legacy players may carry free-text style values — surface them as options
  // so editing doesn't blank them out.
  const BAT_OPTS = ["Right Hand", "Left Hand"];
  const BOWL_OPTS = ["Right-arm Fast", "Left-arm Fast", "Right-arm Medium", "Left-arm Medium", "Off Spin", "Leg Spin", "Left-arm Orthodox", "Not Applicable"];
  const withCurrent = (opts, v) => (v && !opts.includes(v) ? [...opts, v] : opts);
  // Iter27: per-category base price falls in as the default when a category is
  // selected (or when the form is opened for a fresh player).
  const categoryBase = (cat) => {
    const cfg = auction.settings?.category_config?.[cat];
    if (cfg && cfg.base_price != null && cfg.base_price !== "") return Number(cfg.base_price);
    return null;
  };
  const initialCat = initial?.category || "A";
  const initialBase = initial?.base_price ?? categoryBase(initialCat) ?? auction.settings?.default_base_price ?? 10;
  const [form, setForm] = useState({
    name: initial?.name || "",
    role: initial?.role || "Batsman",
    category: initialCat,
    photo_url: initial?.photo_url || "",
    location: initial?.location || "",
    mobile: initial?.mobile || "",
    age: initial?.age || "",
    batting_style: initial?.batting_style || "",
    bowling_style: initial?.bowling_style || "",
    notes: initial?.notes || "",
    base_price: initialBase,
    is_icon: initial?.is_icon || initial?.category === "Icon" || false,
    icon_team_id: initial?.icon_team_id || initial?.sold_to || "",
  });
  const submit = async (e) => {
    e.preventDefault();
    await onSave({
      ...form,
      category: form.is_icon ? "Icon" : form.category,
      base_price: Number(form.base_price),
      age: form.age ? Number(form.age) : null,
      is_icon: !!form.is_icon,
      icon_team_id: form.is_icon ? (form.icon_team_id || null) : null,
    });
  };
  return (
    <form id={formId} onSubmit={submit} data-testid="player-form" className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-5">
        <div>
          <label className={labelCls}>Name{Req}</label>
          <input required data-testid="input-player-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={fieldCls} />
        </div>
        <div>
          <label className={labelCls}>Role{Req}</label>
          <select data-testid="select-player-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={selectCls}>
            {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Category</label>
          <select data-testid="select-player-category" value={form.is_icon ? "Icon" : form.category} onChange={(e) => {
            const v = e.target.value;
            // When user picks a category with a per-category base override, sync base_price too.
            const catBase = categoryBase(v);
            setForm({
              ...form,
              category: v,
              is_icon: v === "Icon",
              base_price: catBase != null ? catBase : form.base_price,
            });
          }} className={selectCls}>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c === "Icon" ? "⭐ Icon" : c}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Base Price (pts)</label>
          <input type="number" min="0" data-testid="input-base-price" value={form.base_price} onChange={(e) => setForm({ ...form, base_price: e.target.value })} className={fieldCls} />
        </div>
        {form.is_icon && (
          <div className="sm:col-span-2 bg-amber-500/10 border border-amber-500/40 rounded-md p-3 space-y-2" data-testid="icon-team-section">
            <div className="label-mono !text-amber-400">Icon Player · pre-assigned team (optional)</div>
            <p className="text-xs text-slate-400">Pick a team to auto-sell this Icon player at <strong className="text-emerald-400">0 pts</strong>. Leave empty to auction the Icon player normally when Icon Auction is on.</p>
            <select
              value={form.icon_team_id}
              onChange={(e) => setForm({ ...form, icon_team_id: e.target.value })}
              className={selectCls}
              data-testid="select-icon-team"
            >
              <option value="">— No pre-assigned team (auction normally) —</option>
              {teams.map((t) => (
                <option key={t.id} value={t.id}>{t.name} · {t.short_name}</option>
              ))}
            </select>
          </div>
        )}
        <div>
          <label className={labelCls}>Location</label>
          <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="e.g. Nashik" className={fieldCls} />
        </div>
        <div>
          <label className={labelCls}>Mobile</label>
          <input type="tel" inputMode="tel" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} data-testid="input-player-mobile" placeholder="+91-..." className={fieldCls} />
        </div>
        <div>
          <label className={labelCls}>Age (Years)</label>
          <input type="number" min="1" max="100" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} data-testid="input-player-age" className={fieldCls} />
        </div>
        <div>
          <label className={labelCls}>Batting Style</label>
          <select value={form.batting_style} onChange={(e) => setForm({ ...form, batting_style: e.target.value })} className={selectCls} data-testid="select-batting-style">
            <option value="">Select batting style</option>
            {withCurrent(BAT_OPTS, form.batting_style).map((o) => <option key={o}>{o}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Bowling Style</label>
          <select value={form.bowling_style} onChange={(e) => setForm({ ...form, bowling_style: e.target.value })} className={selectCls} data-testid="select-bowling-style">
            <option value="">Select bowling style</option>
            {withCurrent(BOWL_OPTS, form.bowling_style).map((o) => <option key={o}>{o}</option>)}
          </select>
        </div>
        <div className="sm:col-span-2">
          <ImageInput
            label="Player Photo"
            value={form.photo_url}
            onChange={(v) => setForm({ ...form, photo_url: v })}
            testId="player-photo-input"
            maxKB={5120}
            aspect="aspect-[3/4]"
            dark
          />
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls}>Name Of Club</label>
          <textarea rows="2" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={`${fieldCls} py-2.5`} />
        </div>
      </div>

      {!isEdit && (
        <div className="text-xs text-slate-400 border border-white/10 rounded-md p-3 bg-white/5">
          Default base price: <span className="text-emerald-400 font-mono">{auction.settings?.default_base_price || 10} pts</span> · adjust above for this player.
        </div>
      )}
    </form>
  );
}

export default function PlayersTab({ auction }) {
  const { user } = useAuth();
  const isSuperadmin = user?.role === "superadmin";
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [editPlayer, setEditPlayer] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [p, t] = await Promise.all([
        api.get(`/auctions/${auction.id}/players`),
        api.get(`/auctions/${auction.id}/teams`),
      ]);
      setPlayers(p.data);
      setTeams(t.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [auction.id]);

  const filtered = players.filter((p) => {
    if (status !== "all" && p.status !== status) return false;
    if (q && !p.name.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  const add = async (payload) => {
    setBusy(true);
    try { await api.post(`/auctions/${auction.id}/players`, payload); toast.success("Player added"); setShowAdd(false); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); throw e; }
    finally { setBusy(false); }
  };

  const saveEdit = async (payload) => {
    if (!editPlayer) return;
    setBusy(true);
    try {
      await api.patch(`/players/${editPlayer.id}`, payload);
      toast.success("Player updated");
      setEditPlayer(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(`/auctions/${auction.id}/players/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.inserted} players`);
      if (data.errors?.length) console.warn(data.errors);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed"); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };

  const downloadTemplate = () => {
    window.open(`${API}/players/template`, "_blank");
  };

  const hideToggle = async (p) => {
    const newStatus = p.status === "hidden" ? "available" : "hidden";
    try { await api.patch(`/players/${p.id}`, { status: newStatus }); toast.success(`${p.name} · ${newStatus}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const cancelSale = async (p) => {
    if (!window.confirm(`Cancel sale for ${p.name}? Player will be returned to pool and team refunded.`)) return;
    try { await api.post(`/auctions/${auction.id}/cancel-sale/${p.id}`); toast.success("Sale cancelled"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const reauctionSingle = async (p) => {
    try { await api.post(`/auctions/${auction.id}/reauction/${p.id}`); toast.success(`${p.name} eligible for re-auction`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const reauctionAll = async () => {
    const unsoldCount = players.filter((x) => x.status === "unsold").length;
    if (unsoldCount === 0) { toast("No unsold players"); return; }
    if (!window.confirm(`Move all ${unsoldCount} unsold players back to available for a re-auction round?`)) return;
    try {
      const { data } = await api.post(`/auctions/${auction.id}/reauction`);
      toast.success(`${data.count} players ready for re-auction`);
      setStatus("available");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete ${p.name}?`)) return;
    try { await api.delete(`/players/${p.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="players-tab">
      <div className="flex justify-between gap-2 items-center flex-wrap">
        <div className="label-mono">
          PLAYERS · {players.length}
          {players.filter((x) => x.status === "unsold").length > 0 && (
            <span className="ml-3 text-amber-600">{players.filter((x) => x.status === "unsold").length} unsold</span>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          {players.some((x) => x.status === "unsold") && (
            <button onClick={reauctionAll} className="btn-ghost text-xs flex items-center gap-2 !border-amber-500/40 !text-amber-600" data-testid="reauction-all-button">
              <ArrowClockwise size={14} /> Re-auction Round
            </button>
          )}
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={upload} className="hidden" data-testid="excel-file-input" />
          {isSuperadmin && (
            <>
              <button onClick={downloadTemplate} className="btn-ghost text-xs flex items-center gap-2" data-testid="download-template-button">
                <DownloadSimple size={14} /> Template
              </button>
              <button onClick={() => fileRef.current?.click()} className="btn-ghost text-xs flex items-center gap-2" data-testid="upload-excel-button">
                <UploadSimple size={14} /> Upload
              </button>
            </>
          )}
          <button onClick={() => setShowAdd(true)} className="btn-primary text-xs flex items-center gap-2" data-testid="add-player-button">
            <Plus size={14} /> Add Player
          </button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="relative flex-1">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={16} />
          <input data-testid="players-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name" className="w-full bg-white border border-gray-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none rounded pl-9 pr-4 py-2 text-sm" />
        </div>
        <div className="flex gap-1 overflow-x-auto">
          {STATUSES.map((s) => (
            <button key={s} onClick={() => setStatus(s)} data-testid={`filter-status-${s}`} className={`text-xs font-bold uppercase tracking-widest px-3 py-2 rounded border whitespace-nowrap ${status === s ? "bg-blue-600 text-white border-blue-600" : "border-gray-200 text-gray-500 hover:text-blue-600"}`}>{s}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="label-mono py-16 text-center">Loading players…</div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-gray-200 rounded p-10 text-center text-gray-500" data-testid="players-empty">
          No players. {isSuperadmin ? "Upload an Excel file or add players manually." : "Add players manually to get started."}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-testid="player-grid">
          {filtered.map((p) => (
            <div key={p.id} className="bg-white border border-gray-200 rounded-md overflow-hidden hover:border-gray-300 transition-colors" data-testid={`player-card-${p.id}`}>
              <PlayerPhoto src={p.photo_url} alt={p.name}>
                <div className="flex justify-between items-start">
                  <span className="text-[10px] font-bold uppercase tracking-widest bg-black/60 backdrop-blur px-2 py-1 rounded border border-white/10">{p.role}</span>
                  <div className="flex flex-col items-end gap-1">
                    <StatusBadge status={p.status} />
                    {p.player_number != null && (
                      <span
                        className="bg-black/70 backdrop-blur border border-emerald-500/50 text-emerald-700 rounded px-1.5 py-0.5 text-[10px] font-bold font-mono"
                        data-testid={`player-number-${p.id}`}
                      >
                        #{p.player_number}
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <div
                    className="font-display text-lg leading-tight uppercase text-white"
                    style={{ textShadow: "0 2px 6px rgba(0,0,0,0.85), 0 0 3px rgba(0,0,0,0.9)" }}
                  >{p.name}</div>
                  <div className="label-mono text-gray-600">{p.location || "—"} · Cat {p.category}</div>
                </div>
              </PlayerPhoto>
              <div className="p-3 flex items-center justify-between gap-2">
                <div>
                  <div className="label-mono">{p.status === "sold" ? "Sold" : "Base"}</div>
                  <div className={`font-display text-xl leading-none ${p.status === "sold" ? "text-emerald-600" : ""}`}>
                    {p.status === "sold" ? p.sold_price : p.base_price}<span className="text-xs text-gray-500 ml-1">pts</span>
                  </div>
                </div>
                <div className="flex gap-1">
                  {p.status !== "sold" && (
                    <button onClick={() => setEditPlayer(p)} title="Edit" className="p-2 rounded hover:bg-gray-100" data-testid={`edit-player-${p.id}`}>
                      <PencilSimple size={14} className="text-emerald-600" />
                    </button>
                  )}
                  {p.status === "sold" && (
                    <button onClick={() => cancelSale(p)} title="Cancel sale" className="p-2 rounded hover:bg-amber-950" data-testid={`cancel-sale-${p.id}`}>
                      <ArrowClockwise size={14} className="text-amber-600" />
                    </button>
                  )}
                  {p.status === "unsold" && (
                    <button onClick={() => reauctionSingle(p)} title="Re-auction" className="p-2 rounded hover:bg-emerald-950" data-testid={`reauction-${p.id}`}>
                      <ArrowClockwise size={14} className="text-emerald-600" />
                    </button>
                  )}
                  {p.status !== "sold" && (
                    <>
                      <button onClick={() => hideToggle(p)} title={p.status === "hidden" ? "Show" : "Hide"} className="p-2 rounded hover:bg-gray-100" data-testid={`hide-player-${p.id}`}>
                        <EyeSlash size={14} className="text-gray-500" />
                      </button>
                      <button onClick={() => del(p)} title="Delete" className="p-2 rounded hover:bg-red-950" data-testid={`delete-player-${p.id}`}>
                        <Trash size={14} className="text-red-600" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={showAdd}
        onClose={() => setShowAdd(false)}
        title="Add Player"
        subtitle="New Player"
        size="lg"
        variant="dark"
        testId="add-player-modal"
        footer={
          <>
            <button type="button" onClick={() => setShowAdd(false)} className="px-5 py-3 rounded-md bg-slate-700 border border-slate-600 text-white text-xs font-black tracking-widest uppercase hover:bg-slate-600 transition" data-testid="cancel-add-player">Cancel</button>
            <button type="submit" form="player-form-el" disabled={busy} data-testid="submit-player" className="px-5 py-3 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-xs font-black tracking-widest uppercase transition disabled:opacity-60">
              {busy ? "Adding…" : "Add Player"}
            </button>
          </>
        }
      >
        <PlayerForm auction={auction} onSave={add} formId="player-form-el" teams={teams} />
      </Modal>

      {editPlayer && (
        <Modal
          open={!!editPlayer}
          onClose={() => setEditPlayer(null)}
          title={`Edit ${editPlayer.name}`}
          subtitle="Player Details"
          size="lg"
          variant="dark"
          testId="edit-player-modal"
          footer={
            <>
              <button type="button" onClick={() => setEditPlayer(null)} className="px-5 py-3 rounded-md bg-slate-700 border border-slate-600 text-white text-xs font-black tracking-widest uppercase hover:bg-slate-600 transition" data-testid="cancel-edit-player">Cancel</button>
              <button type="submit" form="edit-player-form-el" disabled={busy} data-testid="submit-edit-player" className="px-5 py-3 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-xs font-black tracking-widest uppercase transition disabled:opacity-60">
                {busy ? "Saving…" : "Save Changes"}
              </button>
            </>
          }
        >
          <PlayerForm
            auction={auction}
            initial={editPlayer}
            onSave={saveEdit}
            formId="edit-player-form-el"
            isEdit
            teams={teams}
          />
        </Modal>
      )}
    </div>
  );
}
